import { useEffect, useState } from 'react';
import { AlbumPickerCombobox, Button, Icon, type AlbumPickerOption } from '../../ui/index.js';

export interface MovePhotosDialogProps {
  open: boolean;
  // Number of photos that will be moved (used in copy + button label).
  count: number;
  // Album options the user can pick from. Caller is responsible for
  // pre-filtering: only intra-root candidates, source album excluded.
  options: AlbumPickerOption[];
  onClose: () => void;
  onConfirm: (destinationAlbumId: string) => Promise<void> | void;
  // Optional context line shown above the picker (e.g. "Stesso albero radice").
  hint?: string;
}

// Photo-app style overlay: dimmed backdrop, centered card with a single
// task (pick a destination + confirm). Esc closes; clicking the backdrop
// closes too. Designed to replace the inline <select> in AlbumDetailPage
// for both single-album move and bulk photo move.
export function MovePhotosDialog({
  open,
  count,
  options,
  onClose,
  onConfirm,
  hint,
}: MovePhotosDialogProps) {
  const [destId, setDestId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setDestId(null);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  async function handleConfirm() {
    if (!destId) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(destId);
      // Caller is expected to close on success — but be defensive.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'errore');
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Sposta foto"
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy/60 backdrop-blur-sm sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-t-3xl border border-outline bg-surface-container p-6 shadow-elevated sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
              <Icon name="drive_file_move" className="text-xl" filled />
            </span>
            <div>
              <h2 className="font-display text-lg font-semibold">
                {count === 1 ? 'Sposta 1 foto' : `Sposta ${count} foto`}
              </h2>
              <p className="text-xs text-on-surface-variant">
                {hint ?? 'Scegli un album di destinazione.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Chiudi"
            className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface disabled:opacity-50"
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="mt-5">
          <AlbumPickerCombobox
            options={options}
            value={destId}
            onChange={setDestId}
            placeholder="Cerca un album di destinazione…"
            disabled={submitting}
          />
          {options.length === 0 ? (
            <p className="mt-3 text-xs text-on-surface-variant">
              Nessun album disponibile come destinazione.
            </p>
          ) : null}
        </div>

        {error ? <p className="mt-3 text-sm text-error">{error}</p> : null}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!destId || submitting}
            loading={submitting}
            leftIcon={<Icon name="drive_file_move" />}
          >
            Sposta qui
          </Button>
        </div>
      </div>
    </div>
  );
}
